import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Collapse,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Publish as PublishIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  FilterList as FilterIcon,
  Category as CategoryIcon,
  Link as LinkIcon,
  ExpandLess,
  ExpandMore,
  Public as PublicIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { sidebarColors } from '../theme';

const DRAWER_WIDTH = 260;

// TMT/Upshop brand colors
const brandTeal = '#006261';
const brandTealDark = '#004D4C';
const brandTealLight = '#4DB6AC';
const background = '#F2F5F4';

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { label: 'Dashboard', path: '/', icon: <HomeIcon /> },
  { label: 'Publish', path: '/publish', icon: <PublishIcon /> },
  { label: 'History', path: '/history', icon: <HistoryIcon /> },
  { label: 'Subscribers', path: '/subscribers', icon: <PeopleIcon /> },
  {
    label: 'Settings',
    path: '/settings',
    icon: <SettingsIcon />,
    children: [
      { label: 'Query Filters', path: '/settings/query-filters', icon: <FilterIcon /> },
      { label: 'Module Mappings', path: '/settings/module-mappings', icon: <LinkIcon /> },
      { label: 'Release Types', path: '/settings/release-types', icon: <CategoryIcon /> },
      { label: 'Terms', path: '/settings/terms', icon: <MenuBookIcon /> },
    ],
  },
];

export default function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { instance, accounts } = useMsal();

  const user = accounts[0];
  const userName = user?.name || user?.username || 'User';
  const userEmail = user?.username || '';

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavClick = (path: string, hasChildren?: boolean) => {
    if (hasChildren) {
      setSettingsOpen(!settingsOpen);
    } else {
      navigate(path);
      setDrawerOpen(false);
    }
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    instance.logoutRedirect();
  };

  const isSelected = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sidebar Header - darker teal */}
      <Box sx={{ 
        backgroundColor: brandTealDark, 
        minHeight: '64px',
        display: 'flex',
        alignItems: 'center',
        px: 2,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#fff' }}>
          Release Notes
        </Typography>
      </Box>
      <List sx={{ flex: 1, py: 1 }}>
        {navItems.map((item) => (
          <Box key={item.path}>
            <ListItem disablePadding>
              <ListItemButton
                selected={isSelected(item.path)}
                onClick={() => handleNavClick(item.path, !!item.children)}
                sx={{
                  mx: 1,
                  borderRadius: '6px',
                  py: 1.25,
                  px: 1.5,
                  position: 'relative',
                  color: sidebarColors.text,
                  '&.Mui-selected': {
                    backgroundColor: sidebarColors.activeBg,
                    color: '#FFFFFF',
                    fontWeight: 600,
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 6,
                      bottom: 6,
                      width: 3,
                      backgroundColor: brandTealLight,
                      borderRadius: '0 2px 2px 0',
                    },
                  },
                  '&:hover': {
                    backgroundColor: sidebarColors.hoverBg,
                    color: '#FFFFFF',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }}
                />
                {item.children && (settingsOpen ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>
            </ListItem>
            {item.children && (
              <Collapse in={settingsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItemButton
                      key={child.path}
                      selected={isSelected(child.path)}
                      onClick={() => handleNavClick(child.path)}
                      sx={{
                        ml: 4,
                        mr: 1,
                        borderRadius: '6px',
                        py: 1,
                        px: 1.5,
                        position: 'relative',
                        color: sidebarColors.text,
                        '&.Mui-selected': {
                          backgroundColor: sidebarColors.activeBg,
                          color: '#FFFFFF',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 6,
                            bottom: 6,
                            width: 3,
                            backgroundColor: brandTealLight,
                            borderRadius: '0 2px 2px 0',
                          },
                        },
                        '&:hover': {
                          backgroundColor: sidebarColors.hoverBg,
                          color: '#FFFFFF',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                        {child.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={child.label}
                        sx={{ '& .MuiTypography-root': { fontSize: '0.8125rem' } }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>
      <Divider sx={{ borderColor: sidebarColors.border }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: brandTealLight, fontSize: '0.875rem' }}>
            {userName.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: sidebarColors.text }} noWrap>
              {userName}
            </Typography>
            <Typography variant="caption" sx={{ color: sidebarColors.textMuted }} noWrap>
              {userEmail}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: brandTealDark,
          color: '#FFFFFF',
          boxShadow: 'none',
        }}
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Release Notes Manager
          </Typography>
          <IconButton
            onClick={() => window.open('/releases', '_blank')}
            title="View Public Release Notes"
            sx={{ mr: 1, color: 'rgba(255, 255, 255, 0.7)' }}
          >
            <PublicIcon />
          </IconButton>
          <IconButton onClick={handleUserMenuOpen}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: brandTealLight, fontSize: '0.875rem' }}>
              {userName.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleUserMenuClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">{userEmail}</Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Sign out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: brandTeal,
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: '64px',
          backgroundColor: background,
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
